/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TemplateContext } from '../contexts';
import { IFullTemplate, Question } from '../../config/interfaces';

interface IGetRoundSurveyPayload {
  templateConfigId: string;
  unitId?: string;
  includeArchived: boolean;
}

/**
 * Redux slice for managing template-related state using Redux Toolkit's `createSlice`.
 *
 * This slice handles actions related to survey templates, including:
 * - Fetching template configurations
 * - Fetching templates by ID
 * - Managing selected/chosen templates and surveys
 * - Managing questions within templates, including adding, editing, and resetting questions
 * - Fetching and clearing template documents and question banks
 * - Managing loading, error, and success states for all asynchronous template operations
 * - Setting and clearing filters, search, and sort options for templates
 *
 * @property {object} initialState - The initial state for the template context.
 * @property {Function} getRoundSurvey - Initiates fetching a round survey and sets loading state.
 * @property {Function} getRoundSurveySuccess - Handles successful fetch of a round survey.
 * @property {Function} getRoundSurveyFailure - Handles failed fetch of a round survey.
 * @property {Function} clearRoundSurvey - Clears the current round survey from the state.
 * @property {Function} getTemplateByIdSuccess - Handles successful fetch of a template by ID.
 * @property {Function} getTemplateByIdFailure - Handles failed fetch of a template by ID.
 * @property {Function} clearTemplateById - Clears the template fetched by ID from the state.
 * @property {Function} getQuestionBank - Initiates fetching the question bank.
 * @property {Function} getQuestionBankSuccess - Handles successful fetch of the question bank.
 * @property {Function} getQuestionBankFailure - Handles failed fetch of the question bank.
 * @property {Function} addNewQuestion - Initiates adding a new question to a template.
 * @property {Function} addNewQuestionSuccess - Handles successful addition of a new question.
 * @property {Function} addNewQuestionFailure - Handles failed addition of a new question.
 * @property {Function} editSelectedQuestion - Sets the currently selected question for editing.
 * @property {Function} resetEditQuestion - Resets the edit state for questions.
 * @property {Function} updateQuestion - Initiates updating a question in a template.
 * @property {Function} updateQuestionSuccess - Handles successful update of a question.
 * @property {Function} updateQuestionFailure - Handles failed update of a question.
 * @property {Function} setQuestionsOfTemplate - Sets the list of questions for the current template.
 *
 * @example
 * dispatch(setChosenTemplate(template));
 * dispatch(getRoundSurvey({ facilityId: '12345', unitId: 'A.3N' }));
 * dispatch(addNewQuestion(questionData));
 * dispatch(editSelectedQuestion(questionId));
 *
 * @returns {object} The template slice reducer and actions.
 */
const templateSlice = createSlice({
  name: 'template',
  initialState: TemplateContext,
  reducers: {
    getRoundSurvey: (state, action: PayloadAction<IGetRoundSurveyPayload>) => {
      return {
        ...state,
        chosenSurvey: {},
        loading: true
      };
    },
    // TODO: Need to type a chosenSurvey
    getRoundSurveySuccess: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        chosenSurvey: action.payload,
        loading: false
      };
    },
    getRoundSurveyFailure: state => {
      return {
        ...state,
        chosenSurvey: {},
        loading: false
      };
    },
    clearRoundSurvey: state => {
      return {
        ...state,
        chosenSurvey: {}
      };
    },
    getTemplateByIdSuccess: (state, action: PayloadAction<IFullTemplate>) => {
      return {
        ...state,
        templateById: action.payload
      };
    },
    getTemplateByIdFailure: state => {
      return {
        ...state,
        templateById: {} as IFullTemplate
      };
    },
    clearTemplateById: state => {
      return {
        ...state,
        templateById: {} as IFullTemplate
      };
    },
    getQuestionBank: state => {
      return {
        ...state,
        isQuestionListLoading: true
      };
    },
    getQuestionBankSuccess: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        allQuestions: action.payload,
        isQuestionListLoading: false
      };
    },
    getQuestionBankFailure: state => {
      return {
        ...state,
        allQuestions: [],
        isQuestionListLoading: false
      };
    },
    addNewQuestion: (state, action: PayloadAction<Question>) => {
      return {
        ...state,
        loadingQuestion: true
      };
    },
    addNewQuestionSuccess: state => {
      return {
        ...state,
        loadingQuestion: false
      };
    },
    addNewQuestionFailure: state => {
      return {
        ...state,
        loadingQuestion: false
      };
    },
    editSelectedQuestion: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        editQuestion: true,
        selectedQuestionId: action.payload
      };
    },
    resetEditQuestion: state => {
      return {
        ...state,
        editQuestion: false,
        selectedQuestionId: ''
      };
    },
    updateQuestion: (state, action: PayloadAction<Question>) => {
      return {
        ...state,
        loadingQuestion: true
      };
    },
    updateQuestionSuccess: state => {
      return {
        ...state,
        editQuestion: false,
        loadingQuestion: false,
        selectedQuestionId: ''
      };
    },
    updateQuestionFailure: state => {
      return {
        ...state,
        loadingQuestion: false
      };
    },
    setQuestionsOfTemplate: (state, action: PayloadAction<Question[]>) => {
      return {
        ...state,
        questionsOfTemplate: action.payload
      };
    }
  }
});

export const {
  getRoundSurvey,
  getRoundSurveySuccess,
  getRoundSurveyFailure,
  clearRoundSurvey,
  getTemplateByIdSuccess,
  getTemplateByIdFailure,
  clearTemplateById,
  getQuestionBank,
  getQuestionBankSuccess,
  getQuestionBankFailure,
  addNewQuestion,
  addNewQuestionSuccess,
  addNewQuestionFailure,
  editSelectedQuestion,
  resetEditQuestion,
  updateQuestion,
  updateQuestionSuccess,
  updateQuestionFailure,
  setQuestionsOfTemplate
} = templateSlice.actions;

export default templateSlice.reducer;
